import { useState } from "react";
import type { Summary } from "./designTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { useApiResource } from "./useApiResource";

// Collection order mirrors internal/designs' Collection* constants — spec
// §6.3's eight suggested groupings, plus the honest Uncategorized ninth
// bucket for real documents no path/keyword rule can confidently place.
const COLLECTION_ORDER = [
  "North Star",
  "Architecture rulings",
  "Audits and assessments",
  "Implementation plans",
  "Roadmaps",
  "Prototypes",
  "Cortex design system",
  "Supporting diagrams",
  "Uncategorized",
];

// DesignIndex is the spec §6.3 design library index: a collection-grouped
// catalogue over the whole (~51-item, post-collapse — see
// internal/designs' package doc comment) GET /api/design response. Small
// enough, like ADRIndex/RunbookIndex, to stay a plain client-side
// search+filter over the full list rather than URL-synced saved views.
export function DesignIndex({ token }: { token: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Summary[]>(token, repo, "/design");
  const docs = data ?? [];
  const [query, setQuery] = useState("");
  const [collection, setCollection] = useState("");

  if (status === "loading") {
    return (
      <section className="panel">
        <h2>Design library</h2>
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready") {
    return (
      <section className="panel">
        <h2>Design library</h2>
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const needle = query.trim().toLowerCase();
  const filtered = docs.filter((d) => {
    if (collection && d.collection !== collection) return false;
    if (!needle) return true;
    return `${d.title} ${d.sourcePath} ${d.status}`.toLowerCase().includes(needle);
  });

  const byCollection = new Map<string, Summary[]>();
  for (const d of filtered) {
    const group = byCollection.get(d.collection) ?? [];
    group.push(d);
    byCollection.set(d.collection, group);
  }
  const collectionsPresent = COLLECTION_ORDER.filter((c) => byCollection.has(c));
  const collectionOptions = COLLECTION_ORDER.filter((c) => docs.some((d) => d.collection === c));

  return (
    <section className="panel register">
      <h2>Design library</h2>
      <div className="register-toolbar">
        <input
          type="search"
          role="searchbox"
          aria-label="Search design documents"
          placeholder="Search title, path, status…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label>
          Collection{" "}
          <select aria-label="Collection" value={collection} onChange={(e) => setCollection(e.target.value)}>
            <option value="">All collections</option>
            {collectionOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted-note">
        {filtered.length} of {docs.length} documents
      </p>

      {collectionsPresent.map((c) => {
        const items = byCollection.get(c) ?? [];
        return (
          <div key={c}>
            <h3>
              {c} ({items.length})
            </h3>
            <div className="table-scroll">
              <table className="register-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Kind</th>
                    <th>Status</th>
                    <th>Related ADRs</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((d) => (
                    <tr key={d.id} tabIndex={0} onClick={() => navigate(repoPath(repo, "design", d.id))}>
                      <td>{d.title}</td>
                      <td className="mono">{d.kind}</td>
                      <td>{d.status || "—"}</td>
                      <td>{(d.relatedAdrIds ?? []).join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </section>
  );
}
